import { yupResolver } from '@hookform/resolvers/yup';
import { default as Select } from 'antd/es/select';
import type { Dispatch, FormEventHandler, SetStateAction } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import type { InferType } from 'yup';

import { filterOption } from 'utils/antd/filters-option';
import {
  convertFieldToHumanReadable,
  fieldToDisplayableName
} from 'utils/formatters/fieldToDisplayableName';
import { hasValue } from 'utils/hasValue';

import { transformToObject } from '@pages/Payouts/util';
import { useGetCustomerCreditors } from 'api';

import { useOrganisationId } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { FormError } from '@atoms/FormError/FormError';

import { CancelButton } from '../ConfirmCancelDialog';
import {
  RECEIVER_SELECT_TEST_ID,
  RECEIVER_STEP_NEXT_BUTTON
} from '../constants';
import { SpecificField } from '../fields/SpecificField';
import { setFormErrors } from '../formHelpers';
import type { StepProps } from '../steps';

const { Option } = Select;

type stepsDataProps = {
  stepsData: Partial<
    Record<
      | 'selectProduct'
      | 'payoutRequest'
      | 'sender'
      | 'receiver'
      | 'receiverBank'
      | 'summary'
      | 'intermediaryAgent'
      | 'intermediaryAgentAccount',
      any
    >
  >;
  setStepsData: Dispatch<
    SetStateAction<
      Partial<
        Record<
          | 'selectProduct'
          | 'payoutRequest'
          | 'sender'
          | 'receiver'
          | 'receiverBank'
          | 'summary'
          | 'intermediaryAgent'
          | 'intermediaryAgentAccount',
          any
        >
      >
    >
  >;
};

export const ReceiverStep = ({
  onSubmit,
  onCancel,
  fields,
  defaultValues,
  responseField,
  stepsData,
  setStepsData
}: StepProps & stepsDataProps) => {
  const organisationId = useOrganisationId();

  const { data: recentCreditors } = useGetCustomerCreditors(
    Number(organisationId),
    {
      product_id: Number(stepsData.selectProduct.productId),
      schema_id: Number(stepsData.selectProduct.schemaId)
    },
    {
      query: {
        enabled:
          hasValue(stepsData.selectProduct.productId) &&
          hasValue(stepsData.selectProduct.schemaId)
      }
    }
  );

  const validationObject = useMemo(() => {
    if (hasValue(fields)) {
      const fieldsValidation = Object.keys(fields).reduce<
        Record<string, yup.BaseSchema>
      >((acc, fieldName: string) => {
        acc[fieldName] = yup
          .string()
          .label(fieldToDisplayableName(fieldName, 'RECEIVER_FIELD_NAMES'))
          .required();
        return acc;
      }, {});

      return fieldsValidation;
    }
  }, [fields]);

  const schema = yup.object(validationObject).required();
  interface Inputs extends InferType<typeof schema> {
    base: null;
  }

  const form = useForm<Inputs>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: { ...defaultValues }
  });

  if (!fields) return null;

  const onSubmitHandle: FormEventHandler<HTMLFormElement> = async (e) => {
    try {
      await form.handleSubmit(async (data) => {
        await onSubmit(data);
      })(e);
    } catch (error: any) {
      setFormErrors({
        errors: error.error[responseField],
        setError: form.setError,
        fields: Object.keys(schema.fields)
      });
    }
  };

  function handleRecentCreditorChange(savedCreditorId: string) {
    const existingValues = recentCreditors?.data?.find(
      ({ id }) => id === Number(savedCreditorId)
    );
    if (existingValues) {
      Object.entries(existingValues).forEach(([key, value]) => {
        // This is a special case, because in the db we have full_name, in the FE it's called name
        if (key === 'full_name') {
          form.setValue('name', existingValues.full_name);
        }
        if (key === 'creditor_type') {
          form.setValue('type', existingValues.creditor_type);
        }

        if (fields?.[key]) {
          form.setValue(key, value);
        }
      });
    }

    setStepsData((prev) => {
      const creditorDetails = hasValue(existingValues?.creditor_details)
        ? transformToObject(existingValues.creditor_details)
        : {};

      return {
        ...prev,
        receiver: {
          name: existingValues?.full_name,
          type: existingValues?.creditor_type,
          ...existingValues
        },
        intermediaryAgent: creditorDetails.intermediary_agent ?? null,
        intermediaryAgentAccount:
          creditorDetails.intermediary_agent_account ?? null,
        receiverBank: creditorDetails.creditor_account ?? null
      };
    });
  }

  const shouldShowRecentCreditorSection = !!recentCreditors?.data?.length;

  return (
    <div className="flex flex-col items-center justify-center pb-8">
      <form autoComplete="off" className="w-[90%]" onSubmit={onSubmitHandle}>
        {shouldShowRecentCreditorSection && (
          <>
            <span className="pl-2 text-xs">Recent</span>
            <Select
              showSearch
              filterOption={filterOption}
              placeholder="Select a receiver"
              optionFilterProp="label"
              getPopupContainer={(trigger) => trigger.parentNode}
              onSelect={handleRecentCreditorChange}
              className="h-12 w-full"
              data-testid={RECEIVER_SELECT_TEST_ID}
            >
              {recentCreditors?.data?.map((creditor) => {
                const accountIdentifier = creditor.creditor_details?.find(
                  (creditorDetails) =>
                    creditorDetails.key === 'account_number' ||
                    creditorDetails.key === 'iban'
                );

                return (
                  <Option key={creditor.id} label={creditor.full_name}>
                    <div
                      className="flex flex-col text-xs"
                      key={accountIdentifier?.key}
                    >
                      <p>{creditor.full_name}</p>
                      {hasValue(accountIdentifier?.key) && (
                        <p
                          key={accountIdentifier.value}
                          className="text-gray-500"
                        >{`${convertFieldToHumanReadable(accountIdentifier.key)}: ${accountIdentifier.value}`}</p>
                      )}
                    </div>
                  </Option>
                );
              })}
            </Select>
            <hr className="my-6"></hr>
          </>
        )}
        <div className="mt-4 gap-4">
          {Object.keys(fields).map((fieldName) => {
            return (
              <SpecificField
                key={fieldName}
                fieldName={fieldName}
                fieldDefinition={fields[fieldName]}
                form={form}
                data-testid={`create-manual-payout-receiver-${fieldName}`}
                fieldToDisplayableNameSchema="RECEIVER_FIELD_NAMES"
                responseField={responseField}
              />
            );
          })}
        </div>
        <div className="mt-10 flex gap-4">
          <CancelButton onAction={onCancel} />
          <PrimaryButton
            data-testid={RECEIVER_STEP_NEXT_BUTTON}
            size="large"
            htmlType="submit"
            className="w-full"
          >
            Next
          </PrimaryButton>
        </div>
        <FormError message={form.formState.errors.base?.message} />
      </form>
    </div>
  );
};
